import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
);

// ── Admin credentials (change these) ──
const ADMIN_USERNAME = 'kenAdmin';
const ADMIN_PASSWORD = 'KenOnlyy2026!';
const ADMIN_SESSION_KEY = 'ko_admin_session';

interface AdminSession { username: string; loggedInAt: number; }
interface CustomerSession { id: string; email: string; }

interface AuthContextValue {
  // Admin
  adminSession: AdminSession | null;
  adminLogin: (username: string, password: string) => Promise<{ error?: string }>;
  adminLogout: () => void;
  // Customer
  customerSession: CustomerSession | null;
  customerLogin: (email: string, password: string) => Promise<{ error?: string }>;
  customerSignUp: (email: string, password: string) => Promise<{ error?: string }>;
  customerLogout: () => void;
  customerLoading: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [adminSession, setAdminSession] = useState<AdminSession | null>(() => {
    try {
      const raw = localStorage.getItem(ADMIN_SESSION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  const [customerSession, setCustomerSession] = useState<CustomerSession | null>(null);
  const [customerLoading, setCustomerLoading] = useState(true);

  // Restore Supabase session on mount
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      const user = data.session?.user;
      if (user) setCustomerSession({ id: user.id, email: user.email ?? '' });
      setCustomerLoading(false);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_e, session) => {
      const user = session?.user;
      setCustomerSession(user ? { id: user.id, email: user.email ?? '' } : null);
    });
    return () => listener.subscription.unsubscribe();
  }, []);

  // ── Admin auth ──
  const adminLogin = async (username: string, password: string) => {
    if (username === ADMIN_USERNAME && password === ADMIN_PASSWORD) {
      const session: AdminSession = { username, loggedInAt: Date.now() };
      localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(session));
      setAdminSession(session);
      return {};
    }
    return { error: 'Invalid admin credentials.' };
  };

  const adminLogout = () => {
    localStorage.removeItem(ADMIN_SESSION_KEY);
    setAdminSession(null);
  };

  // ── Customer auth ──
  const customerLogin = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return error ? { error: error.message } : {};
  };

  const customerSignUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({ email, password });
    return error ? { error: error.message } : {};
  };

  const customerLogout = async () => {
    await supabase.auth.signOut();
    setCustomerSession(null);
  };

  return (
    <AuthContext.Provider value={{
      adminSession, adminLogin, adminLogout,
      customerSession, customerLogin, customerSignUp, customerLogout, customerLoading,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
